import React, { useEffect, useState } from 'react';
import { fetchMyUplinkSystems, fetchMyUplinkDevicePoints } from '../../services/api';
import { MyUplinkSystem, DataPoint } from '../../types';
import { formatTimeInTZ } from '../../utils/timezone';
import { NIBE_STATUS_PARAM_IDS, decodeNibeStatus } from '../../utils/nibeStatus';
import { ui } from '../../styles/shared';
import DisabledNotice from './DisabledNotice';

interface Props {
    enabled: boolean;
    timezone: string;
    /** null rensar felet. */
    onError: (msg: string | null) => void;
    onGoToStatus: () => void;
}

const unitOf = (p: DataPoint) => p.parameterUnit?.trim() || '';

// Färg på parameternamnet efter vad det verkar handla om
const colourOf = (name: string): string => {
    const n = name.toLowerCase();
    if (n.includes('temp') || n.includes('bt'))  return '#dc3545';
    if (n.includes('hour') || n.includes('time')) return '#6f42c1';
    if (n.includes('degree') || n.includes('minutes')) return '#007bff';
    if (n.includes('power') || n.includes('energy') || n.includes('kwh')) return '#fd7e14';
    if (n.includes('freq')) return '#17a2b8';
    return '#6c757d';
};

const rawValue = (v: unknown) => (
    <span style={{ color: '#aaa', fontWeight: 'normal', fontSize: '11px', marginLeft: '6px' }}>({String(v)})</span>
);

// Visar värdet: uppslagen text om det finns, annars siffran
const renderValue = (p: DataPoint): React.ReactNode => {
    if (p.enumValues?.length && p.value !== null && p.value !== undefined) {
        const enumMatch = p.enumValues.find(e => String(e.value) === String(p.value));
        if (enumMatch) return <>{enumMatch.text}{rawValue(p.value)}</>;
    }
    // NIBE-status är en bitmask som packas upp till text
    if (NIBE_STATUS_PARAM_IDS.has(p.parameterId) && p.value != null) {
        return <>{decodeNibeStatus(Number(p.value))}{rawValue(p.value)}</>;
    }
    // Ingen träff — visa siffran (strVal kan innehålla enheten, t.ex. "10125min")
    return p.value !== null && p.value !== undefined ? String(p.value) : (p.strVal || '—');
};

// Flik: aktuella värden från vald enhet.
const LiveDataTab: React.FC<Props> = ({ enabled, timezone, onError, onGoToStatus }) => {
    const [systems, setSystems] = useState<MyUplinkSystem[]>([]);
    const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
    const [points, setPoints] = useState<DataPoint[]>([]);
    const [pointsLoading, setPointsLoading] = useState(false);
    const [filterText, setFilterText] = useState('');

    // Hämta system och välj första enheten
    useEffect(() => {
        if (!enabled) return;
        fetchMyUplinkSystems()
            .then(s => {
                setSystems(s);
                if (s.length > 0 && s[0].devices.length > 0) setSelectedDeviceId(s[0].devices[0].id);
            })
            .catch(e => onError('Kunde inte hämta systemen: ' + e.message));
    }, [enabled, onError]);

    // Hämta värden när en enhet valts
    useEffect(() => {
        if (!selectedDeviceId) return;
        setPointsLoading(true);
        fetchMyUplinkDevicePoints(selectedDeviceId)
            .then(p => { setPoints(p); onError(null); })
            .catch(e => onError('Kunde inte hämta parametrar: ' + e.message))
            .finally(() => setPointsLoading(false));
    }, [selectedDeviceId, onError]);

    const filteredPoints = points.filter(p =>
        !filterText ||
        p.parameterName.toLowerCase().includes(filterText.toLowerCase()) ||
        p.parameterId.includes(filterText)
    );

    if (!enabled) return <DisabledNotice onGoToStatus={onGoToStatus} />;

    return (
        <div>
            {/* Val av enhet och sökfält */}
            <div style={{ ...ui.card, padding: '14px 20px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <div style={{ flex: '0 0 300px' }}>
                        <label style={{ ...ui.label, display: 'inline' }}>Enhet: </label>
                        <select style={{ ...ui.input, width: 'auto', marginLeft: '8px' }}
                            value={selectedDeviceId}
                            onChange={e => setSelectedDeviceId(e.target.value)}>
                            {systems.flatMap(sys =>
                                sys.devices.map(d => (
                                    <option key={d.id} value={d.id}>
                                        {sys.name} — {d.productName || d.serialNumber}
                                    </option>
                                ))
                            )}
                        </select>
                    </div>
                    <input style={{ ...ui.input, maxWidth: '200px' }}
                        value={filterText} placeholder="Sök parameter…"
                        onChange={e => setFilterText(e.target.value)} />
                    <button onClick={() => selectedDeviceId && fetchMyUplinkDevicePoints(selectedDeviceId).then(p => setPoints(p))}
                        style={ui.btn('#17a2b8')}>↻ Uppdatera</button>
                    <span style={{ color: '#888', fontSize: '13px', marginLeft: 'auto' }}>
                        {filteredPoints.length} / {points.length} parametrar
                    </span>
                </div>
            </div>

            {/* Tabell med värden */}
            {pointsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>Hämtar…</div>
            ) : (
                <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #ddd', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                        <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                                {['ID', 'Parameter', 'Värde', 'Enhet', 'Uppdaterat'].map(h => (
                                    <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 'bold', color: '#555', borderBottom: '1px solid #ddd' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredPoints.length === 0 ? (
                                <tr><td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#888' }}>Inga parametrar hittades</td></tr>
                            ) : filteredPoints.map((p, i) => (
                                <tr key={p.parameterId} style={{ backgroundColor: i % 2 === 0 ? 'white' : '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                                    <td style={{ padding: '8px 12px', color: '#888', fontFamily: 'monospace', fontSize: '12px' }}>{p.parameterId}</td>
                                    <td style={{ padding: '8px 12px' }}>
                                        <span style={{
                                            display: 'inline-block', padding: '2px 8px', borderRadius: '10px', fontSize: '12px',
                                            backgroundColor: `${colourOf(p.parameterName)}20`, color: colourOf(p.parameterName)
                                        }}>
                                            {p.parameterName}
                                        </span>
                                    </td>
                                    <td style={{ padding: '8px 12px', fontWeight: 'bold' }}
                                        title={p.enumValues?.length
                                            ? p.enumValues.map(e => `${e.value}: ${e.text}`).join('\n')
                                            : undefined}>
                                        {renderValue(p)}
                                    </td>
                                    <td style={{ padding: '8px 12px', color: '#666' }}>{unitOf(p)}</td>
                                    <td style={{ padding: '8px 12px', color: '#aaa', fontSize: '12px' }}>
                                        {p.timestamp ? formatTimeInTZ(p.timestamp, timezone) : '—'}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default LiveDataTab;
